import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { canonicalPathToFlattenPath } from '@procuros/datachecks';

import type { ui } from '@procuros/datachecks';
import type { SuccessfulEnrichment } from 'hooks/useGruAgent/types';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { normalizePath } from 'services/webedi/webedi';
import { cn } from 'support/helpers/cn/cn';
import { humanizeString } from 'support/helpers/generic/generic';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { TransactionsRoutes } from 'support/types';
import { mergeWithDefaultComponents } from 'components/Translate/helpers';
import { Translate } from 'components/Translate/Translate';

import { FieldCodeButton } from './components/FieldCodeButton';
import { FieldRow } from './components/FieldRow';
import { extractLineItemPosition, getFieldLabel, getFieldLabelsByPath, groupNotEnrichedByField } from './helpers';

type EnrichmentActionDetailsProps = {
  agentProposal: SuccessfulEnrichment;
  uiConfig: Array<ui.Section> | undefined;
  canonical: Record<string, unknown> | undefined | null;
};

export const EnrichmentActionDetails = ({ agentProposal, uiConfig, canonical }: EnrichmentActionDetailsProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const { service } = useNavigationContext();
  const fieldLabels = useMemo(() => getFieldLabelsByPath(uiConfig), [uiConfig]);

  const { enriched, enriched_count, not_enriched } = agentProposal.payload;

  const handleFieldClick = (jsonpath: string) => {
    const normalizedPath = normalizePath(jsonpath);
    const fieldPath = canonicalPathToFlattenPath(normalizedPath, canonical);
    if (fieldPath) {
      service.scrollToField(fieldPath);
    }
  };

  const title = t('webedi:sidebar.agents.enrichDocuments.title');
  const appliedCount = t('webedi:sidebar.agents.enrichDocuments.appliedCount', { count: enriched_count });

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="inline-flex items-center gap-2.5 px-3 pt-2 pb-1 text-gray-700 hover:bg-procuros-green-50 hover:text-procuros-green-600"
      >
        <ChevronDownIcon
          className={cn('size-4 shrink-0 text-gray-400 transition-transform', isExpanded ? undefined : '-rotate-90')}
          aria-hidden="true"
        />
        <div className="flex flex-1 flex-col items-start">
          <span className="text-sm leading-6 font-normal text-gray-900">{title}</span>
          <span className="text-xs leading-4 text-gray-500">{appliedCount}</span>
        </div>
      </button>

      {isExpanded && (
        <div className="flex pt-1">
          <div className="flex shrink-0 items-stretch px-3 pl-5">
            <div className="w-px bg-gray-200" />
          </div>
          <div className="flex-1 bg-white pr-3">
            <div className="flex flex-col gap-3 text-sm leading-5 text-gray-500">
              {enriched.map((item) => {
                const label = getFieldLabel(fieldLabels, item.jsonpath);
                const documentLink = routeToPage(TransactionsRoutes.viewMessage, {
                  id: item.source_message_id,
                });

                return (
                  <FieldRow key={item.jsonpath} label={label}>
                    <Translate
                      i18nKey="webedi:sidebar.agents.enrichDocuments.foundOnDocument"
                      values={{
                        value: item.value,
                        documentType: humanizeString(item.source_document_type),
                        reference: item.source_external_ref,
                      }}
                      components={mergeWithDefaultComponents({
                        code: <FieldCodeButton jsonpath={item.jsonpath} onFieldClick={handleFieldClick} />,
                        a: <a href={documentLink} target="_blank" rel="noreferrer" />,
                      })}
                    />
                  </FieldRow>
                );
              })}
              {Object.entries(groupNotEnrichedByField(not_enriched, fieldLabels)).map(([label, items]) => (
                <FieldRow key={label} label={label}>
                  {items.map((item, index) => {
                    const position = extractLineItemPosition(item.jsonpath);
                    const displayName = position !== null ? `#${position}` : item.jsonpath;

                    return (
                      <span key={item.jsonpath}>
                        {index > 0 && ', '}
                        <FieldCodeButton jsonpath={item.jsonpath} onFieldClick={handleFieldClick}>
                          {displayName}
                        </FieldCodeButton>
                      </span>
                    );
                  })}{' '}
                  {t('webedi:sidebar.agents.enrichDocuments.notEnrichedSuffix')}
                </FieldRow>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
